import {
	requestApiDelete,
	requestApiGet,
	requestApiPatch,
	requestApiPost,
	requestApiPostNoContent,
} from "../api/BacklogApiEndpoint";
import type { BacklogConfig } from "../contracts/BacklogConfig";
import type { BacklogApiContext } from "../contracts/api/BacklogApiContext";
import type { BacklogResponse } from "../contracts/api/BacklogResponse";
import type { BacklogResponseNoContent } from "../contracts/api/BacklogResponseNoContent";
import type { WatchingItem } from "../entities/WatchingItem";
import type { WatchingItemCount } from "../entities/WatchingItemCount";
import { assert } from "../utils/BacklogUtil";
import type { AddWatchingParams } from "./params/AddWatchingParams";
import type { CountWatchingParams } from "./params/CountWatchingParams";
import type { DeleteWatchingParams } from "./params/DeleteWatchingParams";
import type { GetWatchingListParams } from "./params/GetWatchingListParams";
import type { GetWatchingParams } from "./params/GetWatchingParams";
import type { MarkWatchingAsReadParams } from "./params/MarkWatchingAsReadParams";
import type { UpdateWatchingParams } from "./params/UpdateWatchingParams";

/**
 * Get Watching List ウォッチ一覧の取得
 *
 * Returns list of your watching issues.
 */
export function getWatchingList(
	config: BacklogConfig,
	params: GetWatchingListParams,
	context?: BacklogApiContext,
): Promise<BacklogResponse<WatchingItem[]>> {
	assert(params);
	return requestApiGet({
		config,
		path: "users/:userId/watchings",
		params,
		context,
	});
}

/**
 * Count Watching ウォッチ数の取得
 *
 * Returns the number of your watching issues.
 */
export function countWatching(
	config: BacklogConfig,
	params: CountWatchingParams,
	context?: BacklogApiContext,
): Promise<BacklogResponse<WatchingItemCount>> {
	assert(params);
	return requestApiGet({
		config,
		path: "users/:userId/watchings/count",
		params,
		context,
	});
}

/**
 * Get Watching ウォッチ情報の取得
 *
 * Returns the information about a watching.
 */
export function getWatching(
	config: BacklogConfig,
	params: GetWatchingParams,
	context?: BacklogApiContext,
): Promise<BacklogResponse<WatchingItem>> {
	assert(params);
	return requestApiGet({
		config,
		path: "watchings/:watchingId",
		params,
		context,
	});
}

/**
 * Add Watching ウォッチの追加
 *
 * Adds a watching. User can add a own watching.
 */
export function addWatching(
	config: BacklogConfig,
	params: AddWatchingParams,
	context?: BacklogApiContext,
): Promise<BacklogResponse<WatchingItem>> {
	assert(params);
	return requestApiPost({
		config,
		path: "watchings",
		params,
		context,
	});
}

/**
 * Update Watching ウォッチの更新
 *
 * Updates a watching. User can update own note.
 */
export function updateWatching(
	config: BacklogConfig,
	params: UpdateWatchingParams,
	context?: BacklogApiContext,
): Promise<BacklogResponse<WatchingItem>> {
	assert(params);
	return requestApiPatch({
		config,
		path: "watchings/:watchingId",
		params,
		context,
	});
}

/**
 * Delete Watching ウォッチの削除
 *
 * Deletes a own watching. User can delete a own watching.
 */
export function deleteWatching(
	config: BacklogConfig,
	params: DeleteWatchingParams,
	context?: BacklogApiContext,
): Promise<BacklogResponse<WatchingItem>> {
	assert(params);
	return requestApiDelete({
		config,
		path: "watchings/:watchingId",
		params,
		context,
	});
}

/**
 * Mark Watching as Read ウォッチの既読化
 *
 * Mark a watching as read.
 */
export function markWatchingAsRead(
	config: BacklogConfig,
	params: MarkWatchingAsReadParams,
	context?: BacklogApiContext,
): Promise<BacklogResponse<BacklogResponseNoContent>> {
	assert(params);
	return requestApiPostNoContent({
		config,
		path: "watchings/:watchingId/markAsRead",
		params,
		context,
	});
}
